import { FormDataKeys } from "types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { validateEmail } from "helpers/validations";
import { useState, useRef, useCallback, useMemo } from "react";
import { submitDemoRequest } from "redux/Modals/actions";
import { useOnClickOutside } from "hooks/useOnClickOutside";

export const ERROR_MESSAGES = {
  required: "This field is required",
  invalidEmail: "Please enter a valid email address",
  passwordMismatch: "Passwords do not match",
  passwordMinLength: "Password must be at least 8 characters long",
} as const;

type InputNameTypes = "username" | "password" | "confirmPassword";

interface FormField {
  value: string;
  error: string | null;
  ref: React.RefObject<HTMLDivElement>;
}

interface UseLoginFormProps {
  mode: "login" | "changePassword";
  onSuccess?: () => void;
}

export const useLoginForm = ({ mode, onSuccess }: UseLoginFormProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);

  const [focusedInput, setFocusedInput] = useState<InputNameTypes | null>(null);
  const [showFormSubmissionError, setShowFormSubmissionError] = useState(false);

  const usernameInputRef = useRef<HTMLDivElement>(null);
  const passwordInputRef = useRef<HTMLDivElement>(null);
  const confirmPasswordInputRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Memoized field configurations
  const fields = useMemo<Record<InputNameTypes, FormField>>(
    () => ({
      username: {
        value: username,
        error: usernameError,
        ref: usernameInputRef,
      },
      password: {
        value: password,
        error: passwordError,
        ref: passwordInputRef,
      },
      confirmPassword: {
        value: confirmPassword,
        error: confirmPasswordError,
        ref: confirmPasswordInputRef,
      },
    }),
    [
      username,
      usernameError,
      password,
      passwordError,
      confirmPassword,
      confirmPasswordError,
    ],
  );

  const validateForm = useCallback(() => {
    let isValid = true;

    if (mode === "login") {
      // Validate username (email)
      if (!username) {
        setUsernameError(ERROR_MESSAGES.required);
        isValid = false;
      } else if (!validateEmail(username)) {
        setUsernameError(ERROR_MESSAGES.invalidEmail);
        isValid = false;
      } else {
        setUsernameError(null);
      }

      // Validate password
      if (!password) {
        setPasswordError(ERROR_MESSAGES.required);
        isValid = false;
      } else if (password.length < 8) {
        setPasswordError(ERROR_MESSAGES.passwordMinLength);
        isValid = false;
      } else {
        setPasswordError(null);
      }
    } else {
      // Change password mode
      if (!password) {
        setPasswordError(ERROR_MESSAGES.required);
        isValid = false;
      } else if (password.length < 8) {
        setPasswordError(ERROR_MESSAGES.passwordMinLength);
        isValid = false;
      } else {
        setPasswordError(null);
      }

      if (!confirmPassword) {
        setConfirmPasswordError(ERROR_MESSAGES.required);
        isValid = false;
      } else if (password !== confirmPassword) {
        setConfirmPasswordError(ERROR_MESSAGES.passwordMismatch);
        isValid = false;
      } else {
        setConfirmPasswordError(null);
      }
    }

    return isValid;
  }, [mode, username, password, confirmPassword]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      if (mode === "changePassword") {
        // TODO: Replace with actual password change API call
        const success = true; // Simulate successful password change

        if (success) {
          onSuccess?.();
          navigate("/login");
          return;
        }
        setShowFormSubmissionError(true);
      } else {
        // Original login flow
        const formDataKeys: FormDataKeys = {
          email: username,
          company: "", // Not used in this form
          fullName: password, // Temporary mapping
          calledFrom: "free_trial",
        };

        const success = await dispatch(submitDemoRequest(formDataKeys) as any);

        if (success) {
          navigate("/thank-you");
        }
        setShowFormSubmissionError(!success);
      }
    },
    [validateForm, username, password, dispatch, navigate, mode, onSuccess],
  );

  const handleInputFocus = useCallback((inputName: InputNameTypes | null) => {
    setFocusedInput(inputName);
  }, []);

  const handleUsernameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUsername(e.target.value);
      setUsernameError(null);
      setShowFormSubmissionError(false);
    },
    [],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      setPasswordError(null);
      setShowFormSubmissionError(false);
    },
    [],
  );

  const handleConfirmPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfirmPassword(e.target.value);
      setConfirmPasswordError(null);
      setShowFormSubmissionError(false);
    },
    [],
  );

  const handleClickOutside = useCallback(() => {
    setFocusedInput(null);
  }, []);

  // Apply click outside to each ref (must be called at top level, not in loops)
  useOnClickOutside(
    usernameInputRef,
    handleClickOutside,
    focusedInput === "username",
  );
  useOnClickOutside(
    passwordInputRef,
    handleClickOutside,
    focusedInput === "password",
  );
  useOnClickOutside(
    confirmPasswordInputRef,
    handleClickOutside,
    focusedInput === "confirmPassword",
  );

  return {
    fields,
    focusedInput,
    showFormSubmissionError,
    handleSubmit,
    handleInputFocus,
    handleUsernameChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
  };
};
